import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppSelector } from "~/app/store";
import { activityStateFromPortResponse } from "~/features/autostoreBin/BinState";
import { GetPortResponse } from "~/redux/actions/autostore";

import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import {
  useGetBinQuery,
  useGetPortStatusQuery
} from "~/redux/warehouse/autostoreGrid.hooks";
import { WorkstationSummaryDto } from "~/types/api";

import { useShouldListenToGridEvents } from "./useShouldListenToGridEvents";

const inGridModes = ["G", "C", "F", "R", "X", "T"];

/**
 * @deprecated see usePortStatusQuery for the preferred usage
 */
const usePortStatus = (
  portStateByPort: {
    [portId: number]: {
      getPortResponse: GetPortResponse;
      timestamp: Date;
    };
  },
  siteAllPortIds: number[],
  currentSelectedPortId?: number
) => {
  return useMemo(() => {
    const portStateByPortArray = Object.values(portStateByPort);

    const areAllPortsReady =
      !!portStateByPortArray.length &&
      siteAllPortIds.length === portStateByPortArray.length &&
      portStateByPortArray.every((port) => port.getPortResponse.isReady);
    const areAllPortsOpen =
      !!portStateByPortArray.length &&
      portStateByPortArray.every(
        (port) => port.getPortResponse.mode === "OPEN"
      );

    const areSomePortsReady =
      !!portStateByPortArray.length &&
      portStateByPortArray.some((port) => port.getPortResponse.isReady);

    const areTwoOrMorePortsReady =
      !!portStateByPortArray.length &&
      portStateByPortArray.filter((port) => port.getPortResponse.isReady)
        .length >= 2;

    const isSelectedBinReady = currentSelectedPortId
      ? portStateByPort[currentSelectedPortId]?.getPortResponse.isReady &&
        portStateByPort[currentSelectedPortId]?.getPortResponse.mode === "OPEN"
      : false;

    const firstPort = portStateByPortArray.length
      ? portStateByPortArray[0]
      : null;

    return {
      portStateByPortArray,
      areAllPortsReady,
      areAllPortsOpen,
      areSomePortsReady,
      areTwoOrMorePortsReady,
      isSelectedBinReady,
      firstPort
    };
  }, [currentSelectedPortId, portStateByPort, siteAllPortIds.length]);
};

export default usePortStatus;

const emptyObject = {};

export const usePortStatusQuery = (
  portId: number | undefined,
  portSideIndex?: number,
  workstationToUse?: WorkstationSummaryDto
) => {
  const thisWorkstation = useAppSelector(selectThisWorkstation);
  const workstation = workstationToUse ? workstationToUse : thisWorkstation;
  const shouldListenToGridEvents = useShouldListenToGridEvents();

  const [portPollingInterval, setPortPollingInterval] = useState(0);
  const [binPollingInterval, setBinPollingInterval] = useState(0);

  const {
    data: portStatus,
    error: portStatusError,
    refetch: refetchPortStatus
  } = useGetPortStatusQuery(
    workstation && portId
      ? {
          autostoreGridId: workstation.autostoreGridId,
          portId
        }
      : skipToken,
    {
      pollingInterval: shouldListenToGridEvents ? portPollingInterval : 500,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, error }) => ({
        data,
        error
      })
    }
  );

  const {
    data: binState,
    error: binStateError,
    isUninitialized: binStateUninitialized,
    refetch: refetchBinState
  } = useGetBinQuery(
    portStatus?.selectedBin && workstation && portId
      ? {
          autostoreGridId: workstation.autostoreGridId,
          binNumber: portStatus?.selectedBin
        }
      : skipToken,
    {
      pollingInterval: shouldListenToGridEvents ? binPollingInterval : 500,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, error, isUninitialized }) => ({
        data,
        error,
        isUninitialized
      })
    }
  );

  const portMode = portStatus?.mode;
  const portIsReady = portStatus?.isReady;
  const portSelectedBin = portStatus?.selectedBin;
  const isPortWaiting =
    portMode !== "CLOSED" && portIsReady === false && portSelectedBin !== 0;

  const binStateMatchesSelectedBin =
    binState?.binState.binId === portStatus?.selectedBin;
  const matchedBinState = binStateMatchesSelectedBin ? binState : undefined;

  const multiportEnabled = workstation?.multiPortEnabled;
  const portSide = matchedBinState?.portSide;
  const binMode = matchedBinState?.binState.binMode;
  const portSideKnown = multiportEnabled ? portSide !== "unknown" : true;
  const isBinWaiting = (binMode && binMode !== "O") || !portSideKnown;

  const binPortSideIndex = multiportEnabled && portSide === "right" ? 1 : 0;

  const binsAbove = matchedBinState?.binsAbove;
  const isXAndYZero =
    matchedBinState?.binState.xPosition === 0 &&
    matchedBinState?.binState.yPosition === 0;
  const inGrid = !!binMode && inGridModes.includes(binMode) && !isXAndYZero;
  const onRobot = isXAndYZero;
  const inPort = binMode === "P" && !isXAndYZero;

  const portIdsMatch =
    portId !== undefined && matchedBinState?.binState.portId === portId;
  const isBinOpen = binMode === "O" && portIdsMatch;

  // Port polling. Polls faster than bin state since it detects bin arrival.
  useEffect(() => {
    if (inPort || onRobot) {
      setPortPollingInterval(500);
    } else if (inGrid) {
      setPortPollingInterval(1000);
    } else if (binsAbove) {
      setPortPollingInterval(2000);
    } else if (isPortWaiting) {
      setPortPollingInterval(500);
    } else {
      setPortPollingInterval(0);
    }
  }, [isPortWaiting, inGrid, binsAbove, onRobot, inPort]);

  // Bin polling
  useEffect(() => {
    if (inPort) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinPollingInterval(500);
    } else if (onRobot) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinPollingInterval(3500);
    } else if (isBinWaiting || inGrid || binsAbove) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinPollingInterval(7000);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    } else if (!isBinWaiting) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinPollingInterval(0);
    }
  }, [isBinWaiting, inGrid, binsAbove, onRobot, inPort]);

  const refetch = useCallback(async () => {
    await refetchPortStatus();
    if (!binStateUninitialized) {
      await refetchBinState();
    }
  }, [refetchPortStatus, refetchBinState, binStateUninitialized]);

  return {
    ...portStatus,
    ...(portStatus?.selectedBin && matchedBinState
      ? { binPortSideIndex, binState: matchedBinState }
      : emptyObject),
    activityState: activityStateFromPortResponse(
      portStatus,
      typeof portSideIndex === "number"
        ? portSideIndex === binPortSideIndex
        : undefined
    ),
    error: portStatusError ?? binStateError,
    inGrid,
    onRobot,
    inPort,
    isBinOpen,
    refetch
  };
};

export type PortStatusQueryResponse = ReturnType<typeof usePortStatusQuery>;
